import React, { useEffect, useMemo, useState } from 'react';
import { listMockSessions } from '../../api/mockSessions';
import { formatInterviewType } from '../../utils/formatters';
import { probeAgent } from '../utils/agentProbe';

export default function CandidatePracticePage() {
  const [sessions, setSessions] = useState([]);
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listMockSessions()
      .then((res) => {
        // Real practice sessions only (no prototype history).
        const raw = Array.isArray(res.data) ? res.data : [];
        setSessions(raw);
      })
      .catch((err) => console.warn('[Practice]', err.message))
      .finally(() => setLoading(false));

    probeAgent().then(setAgent);
  }, []);

  const stats = useMemo(() => {
    const completed = sessions.filter((s) => s.overallScore != null);
    const scores = completed.map((s) => s.overallScore);
    // No sessions means no numbers: never show invented stats.
    const count = sessions.length;
    const avg = scores.length
      ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
      : null;
    const best = scores.length ? Math.round(Math.max(...scores)) : null;

    // Last 8 session scores for trend curve (Bug #6 fix)
    const trendScores = scores.slice(0, 8).reverse();

    return { count, avg, best, trendScores };
  }, [sessions]);

  const openApp = () => {
    window.location.href = 'samastix://practice';
  };

  const formatSessionDate = (isoStr) => {
    if (!isoStr) return 'Recently';
    const d = new Date(isoStr);
    const diffHours = (Date.now() - d.getTime()) / 3600000;
    if (diffHours < 24) {
      return `Today, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (diffHours < 48) {
      return `Yesterday, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Build SVG Path coordinates for the trend chart
  const { pathD, areaD, points } = useMemo(() => {
    const scores = stats.trendScores;
    const width = 800;
    const height = 160;
    const paddingX = 40;
    const paddingY = 25;
    const minScore = 30;
    const maxScore = 100;

    const pts = scores.map((score, idx) => {
      const x = paddingX + (idx / (scores.length - 1)) * (width - paddingX * 2);
      const normalized = (score - minScore) / (maxScore - minScore);
      const y = height - paddingY - normalized * (height - paddingY * 2);
      return { x, y, score };
    });

    if (pts.length < 2) return { pathD: '', areaD: '', points: [] };

    // Create smooth bezier curve
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i === 0 ? 0 : i - 1];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;

      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;

      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }

    const last = pts[pts.length - 1];
    const first = pts[0];
    const aD = `${d} L ${last.x} ${height} L ${first.x} ${height} Z`;

    return { pathD: d, areaD: aD, points: pts };
  }, [stats.trendScores]);

  if (loading) {
    return (
      <div className="py-24 text-center">
        <div className="w-8 h-8 border-2 border-[var(--teal-500)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-[var(--ink-soft)]">Loading practice data…</p>
      </div>
    );
  }

  const lastPoint = points[points.length - 1];

  return (
    <div className="space-y-8">
      {/* ── Page Header ─────────────────────────────────────────────── */}
      <div>
        <h1
          className="text-2xl sm:text-[26px] font-semibold text-[var(--navy-950)] tracking-tight"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          Practice
        </h1>
        <p className="text-[13.5px] text-[var(--ink-soft)] mt-1">
          Mock interviews and your performance trend.
        </p>
      </div>

      {/* ── 3 Stat Tiles Row ────────────────────────────────────────── */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card p-5">
          <div
            className="text-2xl sm:text-3xl font-bold text-[var(--navy-950)] tracking-tight"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {stats.count}
          </div>
          <p className="text-xs text-[var(--ink-soft)] mt-1">
            Mock sessions completed
          </p>
        </div>

        <div className="card p-5">
          <div
            className="text-2xl sm:text-3xl font-bold text-[var(--navy-950)] tracking-tight font-tabular"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {stats.avg ?? '-'}
            {stats.avg != null && <span className="text-base font-normal text-[var(--ink-faint)]">/100</span>}
          </div>
          <p className="text-xs text-[var(--ink-soft)] mt-1">
            Average score
          </p>
        </div>

        <div className="card p-5">
          <div
            className="text-2xl sm:text-3xl font-bold text-[var(--navy-950)] tracking-tight font-tabular"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {stats.best ?? '-'}
            {stats.best != null && <span className="text-base font-normal text-[var(--ink-faint)]">/100</span>}
          </div>
          <p className="text-xs text-[var(--ink-soft)] mt-1">
            Best score
          </p>
        </div>
      </div>

      {/* ── Trend Chart (Bug #6 fix: clean SVG line+area render) ─────── */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3
            className="text-sm font-semibold text-[var(--navy-950)]"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Recent trend · last {stats.trendScores.length} sessions
          </h3>
          <span className="text-xs text-[var(--ink-faint)]">
            Higher is better
          </span>
        </div>

        {stats.trendScores.length < 2 && (
          <p className="text-xs text-[var(--ink-soft)] mb-2">
            Complete at least two scored practice sessions to see your trend.
          </p>
        )}
        <div className="w-full h-44 sm:h-52 relative">
          <svg
            viewBox="0 0 800 160"
            preserveAspectRatio="none"
            className="w-full h-full overflow-visible"
          >
            <defs>
              <linearGradient id="trendAreaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#16b3a3" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#16b3a3" stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {/* Subtle horizontal grid guide lines */}
            <line x1="40" y1="30" x2="760" y2="30" stroke="var(--line-soft)" strokeDasharray="3 3" />
            <line x1="40" y1="80" x2="760" y2="80" stroke="var(--line-soft)" strokeDasharray="3 3" />
            <line x1="40" y1="130" x2="760" y2="130" stroke="var(--line-soft)" strokeDasharray="3 3" />

            {/* Filled Area */}
            {areaD && (
              <path
                d={areaD}
                fill="url(#trendAreaGradient)"
              />
            )}

            {/* Stroke Curve */}
            {pathD && (
              <path
                d={pathD}
                fill="none"
                stroke="var(--teal-500)"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* Highlight Dot on Latest Score */}
            {lastPoint && (
              <g>
                <circle
                  cx={lastPoint.x}
                  cy={lastPoint.y}
                  r="6"
                  fill="#ffffff"
                  stroke="var(--teal-600)"
                  strokeWidth="3.5"
                  className="drop-shadow-sm"
                />
              </g>
            )}
          </svg>
        </div>
      </div>

      {/* ── Start Mock Interview Banner ─────────────────────────────── */}
      <div className="rounded-[var(--radius-lg)] p-6 bg-gradient-shell text-white border border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-card">
        <div>
          <h3
            className="text-base font-semibold text-white tracking-tight"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Start a mock interview
          </h3>
          <p className="text-xs text-[#8593a8] mt-1">
            Voice and coding mocks run in the Samastix desktop agent.
          </p>
        </div>

        <button
          type="button"
          onClick={openApp}
          className="btn btn-primary !py-2.5 !px-6 !text-xs shrink-0"
        >
          Open desktop agent
        </button>
      </div>

      {/* ── Section HISTORY ─────────────────────────────────────────── */}
      <div>
        <p className="eyebrow mb-3">History</p>
        <div className="card overflow-hidden">
          {!sessions.length && (
            <div className="p-6 text-sm text-[var(--ink-soft)]">
              No practice sessions yet. Open the desktop agent to start one.
            </div>
          )}
          {sessions.map((s) => {
            // Bug #7 fix: convert enum like VOICE_ADAPTIVE to readable label
            const modeLabel = formatInterviewType(s.mode);

            return (
              <div key={s.id} className="list-row flex items-center justify-between">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-[10px] bg-[var(--navy-900)] text-white text-xs font-bold flex items-center justify-center shrink-0 shadow-sm">
                    {modeLabel.charAt(0)}
                  </div>

                  <div className="min-w-0">
                    <h4
                      className="text-sm font-semibold text-[var(--navy-950)] truncate"
                      style={{ fontFamily: 'var(--font-heading)' }}
                    >
                      {modeLabel} · {s.totalQuestions || 6} questions
                    </h4>
                    <p className="text-xs text-[var(--ink-soft)] mt-0.5 truncate">
                      {formatSessionDate(s.completedAt)}
                    </p>
                  </div>
                </div>

                <div className="shrink-0 pl-2">
                  <span className="font-tabular text-xs font-semibold text-[var(--ink)]">
                    {s.overallScore != null ? Math.round(s.overallScore) : '-'}
                    <span className="text-[11px] font-normal text-[var(--ink-faint)]">/100</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
